import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { 
  Check as CheckIcon, 
  Close as CloseIcon,
  EventRepeat as RescheduleIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { formatTime } from '../utils/formatters';
import { getAppointmentStatusStyles, getAppointmentStatusLabel, APPOINTMENT_STATUS } from '../constants/statusConfig';

export default function AppointmentCard({ 
  appointment, 
  onConfirm, 
  onCancel, 
  onReschedule,
  onMarkAttended
}) {
  // Determinar status do appointment baseado nos timestamps retornados pela API
  const getStatus = () => {
    if (appointment.cancelledAt) return 'cancelled';
    if (appointment.attendedAt) return 'completed';
    
    // Se passou da hora e não foi atendido = no_show
    const appointmentTime = new Date(appointment.startTime);
    const now = new Date();
    if (now > appointmentTime && !appointment.attendedAt && appointment.confirmedAt) {
      return 'no_show';
    }
    
    if (appointment.confirmedAt) return 'confirmed';
    return 'pending';
  };

  const status = getStatus();

  const statusStyles = getAppointmentStatusStyles(status);

  return (
    <Box
      sx={{
        p: 1,
        mb: 1,
        bgcolor: statusStyles.bgcolor,
        borderRadius: 1,
        border: '2px solid',
        borderColor: statusStyles.borderColor,
        opacity: statusStyles.opacity || 1,
        position: 'relative',
        '&:hover': {
          boxShadow: status !== 'cancelled' ? 2 : 0
        }
      }}
    >
      {/* Horário */}
      <Typography 
        variant="caption" 
        fontWeight="bold" 
        display="block"
        sx={{
          textDecoration: status === 'cancelled' ? 'line-through' : 'none',
          color: status === 'cancelled' ? 'text.disabled' : 'text.primary'
        }}
      >
        {formatTime(appointment.startTime)}
      </Typography>

      {/* Cliente */}
      <Typography variant="caption" display="block" noWrap>
        {appointment.clientName}
      </Typography>

      {/* Serviço */}
      {appointment.service?.name && (
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {appointment.service.name}
        </Typography>
      )}

      {/* Profissional e Local */}
      {appointment.schedule && (
        <>
          <Typography variant="caption" color="primary.main" display="block" noWrap>
            {appointment.schedule.professionalName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap fontSize="0.65rem">
            📍 {appointment.schedule.placeName}
          </Typography>
        </>
      )}

      {/* Status Chip */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
        <Chip 
          label={getAppointmentStatusLabel(status)} 
          size="small"
          color={statusStyles.chipColor}
          sx={{ height: 18, fontSize: '0.65rem' }}
        />

        {/* Botões de Ação */}
        <Box display="flex" gap={0.5}>
          {status === 'pending' && (
            <>
              <Tooltip title="Confirmar">
                <IconButton 
                  size="small" 
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfirm(appointment);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancelar">
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(appointment);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {status === 'confirmed' && (
            <>
              <Tooltip title="Marcar como Atendido">
                <IconButton 
                  size="small" 
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkAttended(appointment);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancelar">
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(appointment);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {status === 'no_show' && (
            <Tooltip title="Marcar como Atendido">
              <IconButton 
                size="small" 
                color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAttended(appointment);
                }}
                sx={{ p: 0.5 }}
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {status === 'cancelled' && (
            <Tooltip title="Reagendar">
              <IconButton 
                size="small" 
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onReschedule(appointment);
                }}
                sx={{ p: 0.5 }}
              >
                <RescheduleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
}
